<template>
  <div class="device-track-replay-container">
    <Control
      @replay="search"
      @locateTo="locateTo"
      :i18nPath="i18nPath"
      :model="model"
      :rules="rules"
      :loading="loading"
    />

    <BMap ref="bmap" @loaded="bmapLoaded" />

    <ProgressBar
      ref="progressBar"
      v-bind="progressBarConfig"
      :speed="model.speed"
      @change="handleChange"
      @jumpto="handleJumpto"
    />

    <div class="data-panel-layer">
      <SensorData v-if="sensors.length" />
      <AlarmTable />
    </div>
  </div>
</template>

<script>
import BMap from '../components/BMapGL';
import AlarmTable from '../components/AlarmTable';
import SensorData from '../components/SensorData';
import Control from '../components/Control';

import ProgressBar from '@/components/progressBar';

import ctt from '@/utils/const/index';

import { View, LineRainbowLayer } from 'mapvgl';

import { isEmpty } from '@/utils/is';

import {
  findIndexByRange,
  getRange,
  // toBmapCoordinate,
  toBmapPos
} from '@/utils/bmapUtil';
import { awaitTo } from '@/utils/utils';

import { getGDReplayData } from '@/api/query';
import { toEntries } from '@/utils/transform';
import { toTimestamp } from '@/utils/date';

export default {
  components: {
    BMap,
    Control,
    AlarmTable,
    SensorData,
    ProgressBar
  },
  provide() {
    return {
      getSensors: () => {
        return this.sensors;
      },
      getAlarmList: () => {
        return this.alarmList;
      }
    };
  },
  data() {
    let { beginTime, endTime, sn } = this.$route.query;
    beginTime = +beginTime;
    endTime = +endTime;

    return {
      i18nPath: 'query',
      deviceType: '',

      beginIconLayer: null,
      bmapRef: null,
      lineLayer: null,
      view: null,
      heatGridLayer: null,
      loading: false,
      progressBarConfig: {
        frame: 1,
        maskBg: 'linear-gradient(rgb(0,0,0, 0.3), rgba(0,0,0, 0.8))',
        range: [beginTime, endTime],
        cacheRanges: []
      },
      rules: {
        speed: [{ required: true, message: '请选择回放速度', trigger: 'change' }]
      },
      dataCache: {},
      model: {
        speed: 2,
        beginTime: beginTime,
        endTime: endTime,
        sn
      },
      modMap: {
        [ctt.GD_606]: 'gd606Mod',
        [ctt.GD_612]: 'gd612Mod'
      },
      sensors: [],
      alarmList: [],
      currIndex: -1,
      lastIndex: 0,
      geojson: [
        {
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      ],
      colors: []
    };
  },
  methods: {
    locateTo() {
      if (!this.firstPoint) return;

      this.bmapRef.locateTo({ center: this.firstPoint, zoom: 21, tilt: 40 });
    },
    async search() {
      const [err, res] = await awaitTo(getGDReplayData(this.model));
      if (err) {
        console.error(err);
        return;
      }

      const { data } = isEmpty(res.data) ? { data: [] } : res.data;

      toEntries(data).forEach(e => {
        this.dataCache[toTimestamp(e[0])] = e[1];
      });

      const obj = Object.keys(this.dataCache);

      this.progressBarConfig.cacheRanges.splice(0, Infinity, [obj[0], obj[obj.length - 1]]);
    },
    async handleChange(time) {
      const times = Object.keys(this.dataCache);
      const range = getRange(time, 3);
      const index = findIndexByRange(times, range);

      if (index === -1 || this.currIndex === index) return null;

      this.currIndex = index;

      if (this.lastIndex > this.currIndex) {
        this.resetGeojson();
        this.lastIndex = 0;
      }

      /** @type {{longitude: number,latitude: number, gpsType: number, state: number, sn: string, sensors: any[]}} */
      const list = Object.values(this.dataCache).slice(this.lastIndex, index).filter(Boolean);

      this.lastIndex = index;

      this.updateData(list);

      // const coordinates = list.map(e => [e.longitude, e.latitude]);
      // const p = await toBmapCoordinate({ coordinates });
      // const max = (list.slice(-1)[0] || {}).max;
      // if (max && max !== this.max) this.max = max;

      // const data = list.map((e, i) => {
      //   if (!p[i]) return false;
      //   return toPointGeoJson(p[i], (e.tvocPid / e.max) * 100);
      // });
    },
    handleJumpto() {
      // timer(() => this.map.updateInterval());
    },
    addGeojson(data) {
      const line = this.geojson[0].geometry.coordinates;

      const coord = [data.longitude, data.latitude];

      line.push(coord);

      this.colors.push([0, '0'].includes(data.state) ? '#3faf7c' : '#ff4500');

      this.lineLayer.setData(this.geojson);
    },
    resetGeojson() {
      const line = this.geojson[0].geometry.coordinates;

      line.splice(0, Infinity);

      this.colors.splice(0, Infinity);
    },
    parseCoords(coords) {
      // if (this.$route.query.deviceType === ctt.GD_606) return gprmc2Wgs84(coords);
      return coords;
    },
    updateData(list) {
      if (isEmpty(list)) return;

      this.sensors = list
        .slice(-1)[0]
        .sensors.map(e => ({ name: e.sensorName, value: e.sensorValue, unitName: '' }));

      list = list.filter(e => +e.longitude && +e.latitude);

      if (isEmpty(list)) return;

      const firstPoint = this.parseCoords([list[0].longitude, list[0].latitude]);

      if (!this.firstPoint) {
        toBmapPos({ coordinate: firstPoint }).then(pos => {
          if (!this.beginIconLayer) {
            this.firstPoint = pos;

            return this.addBeginIcon(pos);
          }

          const iconPoint = this.beginIconLayer.getPoint();

          if (pos[1] !== iconPoint.lat && pos[0] !== iconPoint.lng) {
            this.removeBeginIcon();

            this.addBeginIcon(pos);
          }
        });
      }

      list.forEach(data => {
        const coordinate = this.parseCoords([data.longitude, data.latitude]);

        toBmapPos({ coordinate }).then(pos => {
          // this.addPoint(pos[0], pos[1], data.state === 0 ? 50 : 80);

          this.addGeojson({ ...data, longitude: pos[0], latitude: pos[1] });
        });
      });
    },
    bmapLoaded(bmapRef) {
      this.bmapRef = bmapRef;

      this.initLineLayer();

      this.view = new View({
        map: this.bmapRef.bmap
      });

      this.view.addLayer(this.lineLayer);

      // bmapRef.bmap.setTilt(73);
      // bmapRef.bmap.setHeading(64.5);

      // this.registeBmapClick();
    },
    registeBmapClick() {
      this.bmapRef.bmap.addEventListener('click', e => {
        this.addGeojson(
          {
            longitude: e.latlng.lng,
            latitude: e.latlng.lat,
            state: Math.floor(Math.random() * 3)
          },
          0
        );
      });

      // this.bmapRef.bmap.addEventListener('click', e => {
      //   console.log(this.points);
      //   this.addPoint(e.latlng.lng, e.latlng.lat, 100);
      // });
    },
    initLineLayer() {
      this.lineLayer = new LineRainbowLayer({
        style: 'road', // road, arrow, normal
        width: 12,
        color: this.colors
      });
    },
    removeBeginIcon() {
      if (this.beginIconLayer) {
        this.beginIconLayer.remove();
        this.beginIconLayer = null;
      }
    },
    addBeginIcon(coord) {
      if (this.beginIconLayer) return;

      this.bmapRef.locateTo({ center: coord, zoom: 19 });

      function createDOM() {
        var div = document.createElement('div');
        div.innerText = this.properties.text;
        div.setAttribute(
          'style',
          ` width: 26px;
            height: 26px;
            background: #f00;
            color: #fff;
            border-radius: 50%;
            overflow: hidden;
            box-sizing: border-box;
            font-size: 14px;
            text-align: center;
            line-height: 26px;
            box-shadow: 0 0 2px 2px #fff;`
        );

        return div;
      }

      // 创建自定义覆盖物
      this.beginIconLayer = new BMapGL.CustomOverlay(createDOM, {
        point: new BMapGL.Point(...coord),
        opacity: 0.5,
        offsetY: -10,
        properties: { text: '始' }
      });

      this.bmapRef.bmap.addOverlay(this.beginIconLayer);
    }
  },
  created() {
    this.search();
  }
};
</script>

<style lang="scss">
.device-track-replay-container {
  position: relative;

  width: 100%;
  height: 100%;

  .data-panel-layer {
    max-width: 30%;
    height: 580px;
    max-height: 60vh;
    overflow: auto;

    position: absolute;
    right: 0;
    bottom: 65px;
    z-index: 9;

    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    width: 428px;

    padding: 10px;

    .el-table {
      border: unset !important;

      &.el-table--border::after {
        width: 0 !important;
      }

      &::before {
        height: 0 !important;
      }

      th:nth-last-of-type(2),
      td:last-of-type {
        border-right: unset !important;
      }
    }
  }
}
</style>
